import { memoizeWeakKey } from './memoize';

function safeSyncDispose(resource: Disposable): void {
  try {
    resource[Symbol.dispose]();
  } catch (error) {
    reportError(new Error('Automatic disposal failed', { cause: error }));
  }
}

async function safeAsyncDispose(resource: AsyncDisposable): Promise<void> {
  try {
    await resource[Symbol.asyncDispose]();
  } catch (error) {
    reportError(new Error('Automatic async disposal failed', { cause: error }));
  }
}

function safeDispose(resource: Disposable | AsyncDisposable): void {
  if (Symbol.dispose in resource) {
    safeSyncDispose(resource);
  } else if (Symbol.asyncDispose in resource) {
    void safeAsyncDispose(resource);
  }
}

const resourceFinalizer = new FinalizationRegistry(safeDispose);

type UnknownFunction = (...args: unknown[]) => unknown;

const getCachedWrappedDisposeHandler = memoizeWeakKey(
  (resource: Disposable | AsyncDisposable) => {
    return memoizeWeakKey(
      (originalDisposeHandler: UnknownFunction): UnknownFunction => {
        return function (...args) {
          resourceFinalizer.unregister(resource);
          return originalDisposeHandler.apply(resource, args);
        };
      },
    );
  },
);

const resourceProxyHandler: ProxyHandler<Disposable | AsyncDisposable> = {
  get(resource, property, receiver) {
    const value = Reflect.get(resource, property, receiver);

    if (
      typeof value === 'function' &&
      (property === Symbol.dispose || property === Symbol.asyncDispose)
    ) {
      return getCachedWrappedDisposeHandler(resource)(value);
    }

    return value;
  },
};

/**
 * Returns a wrapped disposable resource in a proxy that automatically unregisters
 * and disposes the resource when the wrapped proxy is garbage collected.
 */
export default function autoDispose<
  Resource extends Disposable | AsyncDisposable,
>(resource: Resource): Resource {
  const resourceProxy = new Proxy<Resource>(resource, resourceProxyHandler);

  resourceFinalizer.register(resourceProxy, resource, resource);

  return resourceProxy;
}
